import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { AuditEntry } from "@nh/shared";
import { Badge } from "@/shared/ui/core";
import { Modal } from "@/shared/ui/ui";
import { formatAuditDateTime, prettyJson, roleLabel, statusVariant } from "./format";

export interface AuditEntryDetailProps {
  entry: AuditEntry | null;
  onClose: () => void;
}

/**
 * El renglón completo de la bitácora: lo que la tabla resume, aquí sin
 * recortar. La petición y la respuesta van como JSON con sangría —ya
 * vienen filtradas del servidor: sin contraseñas, tokens ni archivos—.
 *
 * Es el `Modal` del panel (el mismo del expediente de KYC en Usuarios),
 * con su propio scroll: una respuesta larga no empuja el botón de cerrar
 * fuera de la pantalla.
 */
export function AuditEntryDetail({ entry, onClose }: AuditEntryDetailProps) {
  const { t } = useTranslation();
  if (!entry) return null;

  const who = entry.actorEmail ?? entry.subjectEmail;
  const role = roleLabel(entry.actorRole);

  return (
    <Modal open onClose={onClose} title={entry.label} size="xl">
      <div className="grid max-h-[70vh] gap-5 overflow-y-auto pr-1">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={statusVariant(entry.status)} size="sm">
            {entry.status}
          </Badge>
          <span className="break-all font-mono text-[13px] text-ds-ink">
            {entry.method} {entry.path}
          </span>
        </div>

        <dl className="grid gap-x-6 gap-y-3 text-[13px] sm:grid-cols-2">
          <Field label={t("Fecha y hora")}>{formatAuditDateTime(entry.at)}</Field>
          <Field label={t("Tipo")}>
            {entry.kind === "client" ? t("Error del navegador") : t("Petición")}
          </Field>
          <Field label={t("Quién")}>
            {who ?? t("Anónimo")}
            {role && <span className="text-ds-ink-3"> · {t(role)}</span>}
          </Field>
          {entry.actorEmail && entry.subjectEmail && entry.subjectEmail !== entry.actorEmail && (
            <Field label={t("Sobre quién")}>{entry.subjectEmail}</Field>
          )}
          <Field label={t("IP")} mono>
            {entry.ip ?? "—"}
            {entry.country && <span className="font-sans text-ds-ink-3"> · {entry.country}</span>}
          </Field>
          <Field label={t("Duración")}>
            {entry.durationMs !== undefined ? `${entry.durationMs} ms` : "—"}
          </Field>
          <Field label={t("Folio de la petición")} mono>
            {entry.requestId ?? "—"}
          </Field>
          {entry.page && (
            <Field label={t("Pantalla")} mono>
              {entry.page}
            </Field>
          )}
          {(entry.errorCode || entry.errorMessage) && (
            <Field label={t("Motivo")} wide>
              {entry.errorCode && <span className="font-mono">{entry.errorCode}</span>}
              {entry.errorCode && entry.errorMessage && " · "}
              {entry.errorMessage}
            </Field>
          )}
          {entry.userAgent && (
            <Field label={t("Navegador")} wide>
              <span className="break-all text-[12px]">{entry.userAgent}</span>
            </Field>
          )}
        </dl>

        {entry.request !== undefined && <JsonBlock title={t("Petición")} value={entry.request} />}
        {entry.response !== undefined && <JsonBlock title={t("Respuesta")} value={entry.response} />}
        {entry.stack && <JsonBlock title={t("Pila del error")} value={entry.stack} />}
      </div>
    </Modal>
  );
}

function Field({
  label,
  children,
  mono,
  wide,
}: {
  label: string;
  children: ReactNode;
  mono?: boolean;
  wide?: boolean;
}) {
  return (
    <div className={wide ? "sm:col-span-2" : undefined}>
      <dt className="text-[11px] uppercase tracking-ds-label text-ds-ink-3">{label}</dt>
      <dd className={mono ? "break-all font-mono text-[12.5px] text-ds-ink" : "text-ds-ink"}>
        {children}
      </dd>
    </div>
  );
}

function JsonBlock({ title, value }: { title: string; value: unknown }) {
  return (
    <section className="grid gap-1.5">
      <h3 className="text-[12px] font-semibold text-ds-ink-3">{title}</h3>
      <pre className="max-h-80 overflow-auto rounded-2xl border border-ds-border bg-ds-cream-2/50 p-3 font-mono text-[12px] leading-relaxed text-ds-ink">
        {prettyJson(value)}
      </pre>
    </section>
  );
}
